/**
 * AssessmentNavigator
 *
 * Single source of truth for deriving the navigation plan from an assessment config.
 * Every component asks this utility "what's next?" instead of duplicating logic.
 */

/**
 * The question types the authoring form can assign (see create-assessment).
 * `(string & {})` keeps these literals as editor suggestions while still
 * accepting any string the API might send, so cached/legacy values never break
 * the type.
 */
export type CanonicalQType =
  | 'bmc_type'
  | 'multi_select'
  | 'tap_multiple'
  | 'radio'
  | 'observation'
  | 'linear_scale';

export type QType = CanonicalQType | (string & {});

const CANONICAL_Q_TYPES: readonly CanonicalQType[] = [
  'bmc_type',
  'multi_select',
  'tap_multiple',
  'radio',
  'observation',
  'linear_scale',
];

/**
 * Trim + lower-case a q_type and map it to its canonical value.
 *
 * Routing is now decided from q_type alone (tier + q_type), so this is the one
 * place q_type is cleaned. It does NOT sniff substrings or fall back to
 * submission_mode — a record must carry the real q_type (e.g. 'multi_select').
 * Unknown values pass through normalised so they route to the safe default.
 */
export function normalizeQType(value: unknown): QType {
  const key = String(value ?? '')
    .trim()
    .toLowerCase();
  return (CANONICAL_Q_TYPES as readonly string[]).includes(key)
    ? (key as CanonicalQType)
    : key;
}

export interface AssessmentConfig {
  id: number;
  q_type: QType;
  layer_config: string[] | string; // may arrive as JSON string from some paths
  options_as_layer: 0 | 1;
  options_layer_name: string | null;
  has_intervals: 0 | 1;
  same_option_set: OptionDef[] | string; // may arrive as JSON string
  same_options_set?: OptionDef[] | string; // alias for compatibility
  title?: string;
  description?: string;
  duration_months?: number | null;
  language_code?: string | null;
  region?: string | null;
  submission_mode?: string | null;
  classId?: number | null;
  /** sam6_class_levels.id — smm_syllabus_school_plans.class_id */
  class_level_id?: number | null;
  /** Teacher sam6 section — completion only */
  section_id?: number | null;
  has_lesson_plan?: boolean | number | null;
  type?: string | null;
  /** Human-readable class/standard label e.g. "Nursery", "Grade 1" */
  class_label?: string | null;
  show_option_icons?: number | null;
}

export interface OptionDef {
  id?: number;
  option_text?: string;
  label?: string;
  color?: string;
  sort_order?: number;
  is_correct?: boolean;
  score?: number | null;
  points?: number | null;
  has_nested_valuation?: boolean;
}

/** Each step in the navigation plan */
export type LayerKind =
  | 'section' // fetch sections from API (layer_config entry)
  | 'questions' // fetch section items (questions list)
  | 'test'; // navigate to the test component

export interface LayerPlan {
  index: number;
  kind: LayerKind;
  label: string;
}

export interface NavigationPlan {
  layers: LayerPlan[];
  testRoute: 'mcq-test' | 'tap-test';
  resultRoute: 'bmc-result' | 'bmc-multiselect-result' | 'tap-test-result';
  sectionDepth: number;
  optionsAsLayer: boolean;
  optionsLayerName: string;
  hasIntervals: boolean;
  /** Normalized options array — always parsed, never a raw string */
  options: OptionDef[];
}

function navDebugEnabled(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      window.localStorage?.getItem('debugNav') === '1'
    );
  } catch {
    return false;
  }
}

function navDebug(stage: string, data?: Record<string, unknown>) {
  if (!navDebugEnabled()) return;
  console.log('[AssessmentNavigator]', stage, data ?? {});
}

function normalizeSubmissionMode(mode?: string | null): string {
  if (!mode) return '';
  return mode
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

/**
 * Linear-scale / Weekly Observation assessments (interval-driven).
 * Keep in sync with assessment-eligibility.ts and backend
 * intervals/observation-assessment.mjs — DB often stores these as `rating`.
 */
export function isLinearScaleAssessment(config: any): boolean {
  if (!config) return false;
  const qType = normalizeSubmissionMode(config.q_type);
  const submission = normalizeSubmissionMode(
    config.submission_mode || config.submissionMode
  );
  return (
    qType === 'linear_scale' ||
    qType === 'rating' ||
    submission === 'linear_scale' ||
    submission === 'rating'
  );
}

/**
 * Whether to send the teacher through the interval overview screen before the
 * student list. Linear-scale only - every other type keeps its existing flow.
 */
export function shouldShowIntervalScreen(config: any): boolean {
  if (!isLinearScaleAssessment(config)) return false;
  return true;
}

/**
 * Normalize an AssessmentConfig — parse any JSON strings so the rest of the
 * app always works with proper typed objects.
 */
export function normalizeConfig(config: AssessmentConfig): AssessmentConfig {
  const rawSameOptions = parseJsonField<any[]>(
    (config as any)?.same_option_set ?? (config as any)?.same_options_set,
    []
  );
  const normalizedOptions: OptionDef[] = Array.isArray(rawSameOptions)
    ? rawSameOptions.map((opt, idx) => {
        if (!opt || typeof opt !== 'object') return opt;
        const rawScore = opt.score ?? opt.points;
        const numericScore =
          rawScore != null && Number.isFinite(Number(rawScore))
            ? Number(rawScore)
            : null;
        return {
          ...opt,
          score: numericScore,
          points:
            opt.points != null && Number.isFinite(Number(opt.points))
              ? Number(opt.points)
              : numericScore,
          option_text: opt.option_text ?? opt.label ?? '',
          sort_order: opt.sort_order ?? idx,
          color: opt.color ?? '#ffffff',
          is_correct: !!opt.is_correct,
          has_nested_valuation: !!opt.has_nested_valuation,
        };
      })
    : [];

  return {
    ...config,
    layer_config: parseJsonField<string[]>(config.layer_config, []),
    same_option_set: normalizedOptions,
    same_options_set: normalizedOptions,
  };
}

function parseJsonField<T>(
  value: T | string | null | undefined,
  fallback: T
): T {
  if (value == null) return fallback;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return value as T;
}

/**
 * Derive the full navigation plan from an assessment config.
 *
 * Rules:
 * - ALL layer_config entries are section navigation layers
 * - sectionDepth = layer_config.length
 * - After navigating through all section layers, what happens next depends on options_as_layer:
 *
 *   options_as_layer: 1 → the last section's items ARE the questions shown in the layers
 *                         component as a list. Clicking one opens mcq-test with sectionItemId.
 *
 *   options_as_layer: 0 → after the last section layer, fetch questions via getSectionQuestions,
 *                         show them as a list, clicking one opens the test.
 *
 * layer_config: ["category", "Sub-category"], options_as_layer: 1
 *   sectionDepth: 2
 *   Students shows layer_config[0] (category)
 *   Layers shows layer_config[1] (sub-category items as question list)
 *   Click sub-category item → mcq-test with sectionItemId
 *
 * layer_config: ["Domain", "Words"], options_as_layer: 0
 *   sectionDepth: 2
 *   Students shows layer_config[0] (domain)
 *   Layers shows layer_config[1] (word sections)
 *   Click word section → fetch questions → show list → click → tap-test
 */
export function buildNavigationPlan(config: AssessmentConfig): NavigationPlan {
  const normalized = normalizeConfig(config);
  const layerConfig = normalized.layer_config as string[];
  const options = normalized.same_option_set as OptionDef[];

  // Cast to any to bypass strict compile-time literal checks (0 | 1) while preserving robust runtime parsing resilience
  const rawOptionsAsLayer = normalized.options_as_layer as any;
  const optionsAsLayer =
    Number(rawOptionsAsLayer) === 1 ||
    rawOptionsAsLayer === true ||
    rawOptionsAsLayer === '1' ||
    rawOptionsAsLayer === 'true';

  const rawHasIntervals = normalized.has_intervals as any;
  const hasIntervals =
    Number(rawHasIntervals) === 1 ||
    rawHasIntervals === true ||
    rawHasIntervals === '1' ||
    rawHasIntervals === 'true';

  // All layer_config entries are section layers
  const layers: LayerPlan[] = layerConfig.map((label, i) => ({
    index: i,
    kind: 'section' as LayerKind,
    label,
  }));

  // sectionDepth = total number of section layers
  const sectionDepth = layerConfig.length;

  const plan = {
    layers,
    testRoute: resolveTestRoute(normalized.q_type, normalized.type),
    resultRoute: resolveResultRoute(
      normalized.q_type,
      normalized.submission_mode
    ),
    sectionDepth,
    optionsAsLayer,
    optionsLayerName: normalized.options_layer_name ?? 'Milestones',
    hasIntervals,
    options,
  };
  navDebug('buildNavigationPlan', {
    assessmentId: normalized.id,
    qType: normalized.q_type,
    submissionMode: normalized.submission_mode ?? null,
    sectionDepth: plan.sectionDepth,
    optionsAsLayer: plan.optionsAsLayer,
    resultRoute: plan.resultRoute,
  });
  return plan;
}

/**
 * Map q_type → test route segment. Decided from q_type alone — the tier never
 * changes which screen you land on, because each q_type has exactly one screen
 * that renders every tier (2/3/4) from the assessment's own data:
 *
 *   tap_multiple → tap-test   (TapTestComponent)
 *   everything else (bmc_type, multi_select, radio, …) → mcq-test
 *                             (TierAssessmentComponent — the single screen for
 *                              every tier; it reads depth from layer_config and
 *                              tier wording from `type`, not from the URL)
 *
 * linear_scale is intentionally NOT handled here — it runs on its own
 * assessment-level flow (see isLinearScaleAssessment / shouldShowIntervalScreen)
 * and never reaches a section-level test route.
 *
 * The tier-labelled routes (3-tier-test / 4-tier-test) stay registered for old
 * bookmarks but are no longer generated.
 *
 * The second parameter is kept only so existing call sites compile unchanged;
 * it is ignored.
 */
export function resolveTestRoute(
  qType: QType,
  _assessmentType?: string | null
): 'mcq-test' | 'tap-test' {
  return normalizeQType(qType) === 'tap_multiple' ? 'tap-test' : 'mcq-test';
}

/**
 * Map q_type → result route segment. Decided from q_type alone:
 *
 *   tap_multiple  → tap-test-result
 *   multi_select  → bmc-multiselect-result   (multi-select is its own q_type)
 *   everything else (bmc_type, radio, …) → bmc-result
 *
 * submission_mode is deliberately no longer consulted. It defaulted to
 * "all_at_once" for every BMC assessment regardless of select mode, so treating
 * that as multi-select mis-routed ordinary single-select BMCs. Multi-select
 * assessments must carry q_type: 'multi_select' (backfilled for legacy rows).
 *
 * The second parameter is kept only so existing call sites compile unchanged;
 * it is ignored.
 */
export function resolveResultRoute(
  qType: QType,
  _submissionMode?: string | null
): 'bmc-result' | 'bmc-multiselect-result' | 'tap-test-result' {
  const normalizedQType = normalizeQType(qType);

  let resultRoute: 'bmc-result' | 'bmc-multiselect-result' | 'tap-test-result';
  if (normalizedQType === 'tap_multiple') {
    resultRoute = 'tap-test-result';
  } else if (normalizedQType === 'multi_select') {
    resultRoute = 'bmc-multiselect-result';
  } else {
    resultRoute = 'bmc-result';
  }

  navDebug('resolveResultRoute', {
    qType,
    normalizedQType,
    resultRoute,
  });
  return resultRoute;
}

/**
 * Given the current depth, determine what the next action should be.
 */
export function resolveNextAction(
  plan: NavigationPlan,
  currentDepth: number
): 'load-sections' | 'load-questions' | 'navigate-to-test' {
  const nextLayer = plan.layers[currentDepth + 1];
  if (!nextLayer) return 'navigate-to-test';
  if (nextLayer.kind === 'section') return 'load-sections';
  if (nextLayer.kind === 'questions') return 'load-questions';
  return 'navigate-to-test';
}

/** Get the label for a given layer index. Safe — returns fallback if out of range. */
export function getLayerLabel(plan: NavigationPlan, index: number): string {
  return plan.layers[index]?.label ?? `Level ${index + 1}`;
}
